import React from "react";
import { __ } from "services/Translator";
import {
  TeaserHero,
  TeaserSection,
  TeaserGrid,
  FeatureCard,
} from "components/auth/teaser";
import { getVipMedalSrc } from "components/goldenCloverHub/vipTierAssets";

/**
 * RewardsTeaser — the logged-out (stranger) view of /rewards.
 * Sells the Golden Clover loyalty programme with ZERO personal data:
 * no balances, no filled/current tier, no streak counter, no claim buttons.
 * Everything here is a generic, illustrative example designed to convert a
 * visitor into a signed-up player. Real-money framing only.
 *
 * Receives the FullLayout modal props (setModalSignUpPropsData /
 * setModalLoginPropsData) straight through so the Sign-Up / Log-in CTAs open
 * the real auth modals.
 */

// Self-contained inline icons — no sprite / no asset-file dependency.
function TIcon({ name }) {
  const paths = {
    clover: (
      <g transform="translate(12 11)">
        <ellipse cx="0" cy="-5" rx="4" ry="5" />
        <ellipse cx="5" cy="0" rx="5" ry="4" />
        <ellipse cx="0" cy="5" rx="4" ry="5" />
        <ellipse cx="-5" cy="0" rx="5" ry="4" />
        <rect x="-0.6" y="3" width="1.2" height="7" rx="0.6" />
      </g>
    ),
    gift: (
      <g>
        <rect x="4" y="10" width="16" height="10" rx="1.5" />
        <rect x="3" y="7" width="18" height="4" rx="1" />
        <rect x="11" y="7" width="2" height="13" />
        <path d="M12 7c-1-3-5-3-5-1 0 1.6 3 1 5 1zm0 0c1-3 5-3 5-1 0 1.6-3 1-5 1z" />
      </g>
    ),
    ladder: (
      <g>
        <path d="M4 20h4v-4H4v4zm6 0h4v-9h-4v9zm6 0h4V7h-4v13z" />
      </g>
    ),
    trophy: (
      <g>
        <path d="M7 4h10v3a5 5 0 0 1-10 0V4z" />
        <rect x="10" y="12" width="4" height="4" />
        <rect x="7" y="18" width="10" height="2.5" rx="1" />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          d="M7 5H4v2a3 3 0 0 0 3 3m10-5h3v2a3 3 0 0 1-3 3"
        />
      </g>
    ),
    flame: (
      <path d="M13 2c1 3-1 5-2.5 6.5C9 10 8 11.5 8 13.5A6 6 0 0 0 20 14c0-3-2-5-3.5-7 .5 2-.5 3-1.5 3.5C15.5 8 15 4 13 2z" />
    ),
    spin: (
      <g>
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          d="M19 12a7 7 0 1 1-2.1-5"
        />
        <path d="M17 3.5l.7 4.2-4.2-.7z" />
      </g>
    ),
    coin: (
      <g>
        <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M9.5 15.5v-7l5 7v-7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    ),
  };
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      {paths[name] || paths.clover}
    </svg>
  );
}

// Mirrors the real five-tier programme on /vip (VIPRewardsTierTable):
// Iron → Copper → Bronze → Silver → Gold. Thresholds and perks must stay in
// sync with that table — never advertise a tier or benefit that doesn't exist.
const VIP_LADDER = [
  {
    key: "iron",
    name: "Iron Clover",
    perks: ["From 100 Clover Points", "Birthday gift & daily reload"],
  },
  {
    key: "copper",
    name: "Copper Clover",
    perks: ["From 250 Clover Points", "Weekly bonus"],
  },
  {
    key: "bronze",
    name: "Bronze Clover",
    perks: ["From 500 Clover Points", "All earlier perks, bigger rewards"],
  },
  {
    key: "silver",
    name: "Silver Clover",
    perks: ["From 1,000 Clover Points", "Monthly bonus & VIP tournaments"],
  },
  {
    key: "gold",
    name: "Gold Clover",
    perks: ["From 25,000 Clover Points", "Dedicated VIP host & custom program"],
  },
];

// Illustrative medals only — no earned/locked state, no dates, no progress.
const SAMPLE_MEDALS = [
  { icon: "trophy", name: "First Win", hint: "Land your first winning spin" },
  { icon: "flame", name: "Hot Streak", hint: "Play 5 days in a row" },
  { icon: "spin", name: "Reel Roller", hint: "Spin across your favourite slots" },
  { icon: "clover", name: "Lucky Streak", hint: "Win several spins back to back" },
  { icon: "gift", name: "Bonus Hunter", hint: "Trigger bonus rounds" },
  { icon: "trophy", name: "Tournament Top 10", hint: "Finish high on a leaderboard" },
];

export default function RewardsTeaser(props) {
  const { setModalSignUpPropsData, setModalLoginPropsData } = props;
  const cta = { setModalSignUpPropsData, setModalLoginPropsData };

  return (
    <div className="rewards-page rewards-teaser container-xxl">
      <TeaserHero
        eyebrow={__("Golden Clover Rewards")}
        title={__("Join the Golden Clover Rewards programme")}
        subtitle={__(
          "The more you play, the more you earn. Collect Clover Coins on every spin, climb the VIP ladder, and redeem your rewards for free spins and bonuses."
        )}
        align="start"
        {...cta}
      >
        <p className="rewards-teaser__legal">
          {__(
            "18+ only. Play responsibly. Terms & conditions apply. Available where permitted by law."
          )}
        </p>
      </TeaserHero>

      <TeaserSection
        eyebrow={__("How it works")}
        title={__("Earn as you play")}
        subtitle={__(
          "Every wager moves you forward — no coin packages, no catch. Just play the games you love."
        )}
      >
        <TeaserGrid minColWidth={240} gap={18}>
          <FeatureCard
            icon={<TIcon name="coin" />}
            title={__("Earn on every spin")}
            description={__(
              "Collect Clover Coins automatically as you wager on any game — the more you play, the faster they add up."
            )}
          />
          <FeatureCard
            icon={<TIcon name="gift" />}
            title={__("Redeem for real rewards")}
            description={__(
              "Turn your Clover Coins into free spins and bonuses, then take them straight back to the tables."
            )}
          />
          <FeatureCard
            icon={<TIcon name="ladder" />}
            title={__("Climb the VIP ladder")}
            description={__(
              "Rise through the tiers to unlock bigger cashback, faster withdrawals and a dedicated VIP host."
            )}
          />
        </TeaserGrid>
      </TeaserSection>

      <TeaserSection
        eyebrow={__("VIP programme")}
        title={__("Five tiers of clover luck")}
        subtitle={__(
          "Start at Iron and work your way to Gold. Each tier unlocks richer rewards — here's a taste of what's ahead."
        )}
      >
        <ol className="rewards-teaser__ladder" aria-label={__("VIP tiers")}>
          {VIP_LADDER.map((tier) => (
            <li className="rt-tier" key={tier.key}>
              <div className="rt-tier__medal">
                <img src={getVipMedalSrc(tier.key)} alt="" loading="lazy" />
              </div>
              <div className="rt-tier__name">{__(tier.name)}</div>
              <ul className="rt-tier__perks">
                {tier.perks.map((perk) => (
                  <li key={perk}>{__(perk)}</li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      </TeaserSection>

      <TeaserSection
        eyebrow={__("Achievements")}
        title={__("A wall of medals to chase")}
        subtitle={__(
          "Unlock dozens of achievements as you hit milestones across the casino. Below are just a few examples."
        )}
      >
        <TeaserGrid minColWidth={190} gap={16}>
          {SAMPLE_MEDALS.map((medal, i) => (
            <div className="rt-medal" key={`${medal.name}-${i}`}>
              <div className="rt-medal__disc" aria-hidden="true">
                <TIcon name={medal.icon} />
              </div>
              <div className="rt-medal__name">{__(medal.name)}</div>
              <div className="rt-medal__hint">{__(medal.hint)}</div>
            </div>
          ))}
        </TeaserGrid>
      </TeaserSection>

      <TeaserHero
        className="rewards-teaser__closer"
        align="center"
        eyebrow={__("Ready when you are")}
        title={__("Sign up and start earning today")}
        subtitle={__(
          "Create your account in seconds and your first Clover Coins are only a spin away."
        )}
        {...cta}
      >
        <p className="rewards-teaser__legal">
          {__(
            "18+ only. Play responsibly. Terms & conditions apply. Available where permitted by law."
          )}
        </p>
      </TeaserHero>
    </div>
  );
}
